<!--
  The pasted HTML of an announcement, in a shadow root: its <style> styles it alone and the app's CSS
  stays out (only the inherited font, direction and colour come through), so what the root admin
  pasted looks the same in the editor, in the preview and at the customer. The HTML is scrubbed on
  the way in (modules/AnnouncementHtml.js - no scripts, no handlers). Buttons inside it talk to the
  popup through attributes:
    data-planup-action="cta|close|ai|support"   → 'action'
    data-planup-link="/path", <a href="/path">  → 'navigate'
    <a href="https://...">                       → 'link' (opens in a new tab by itself)
-->
<template>
    <div class="announcementHtmlHost"></div>
</template>

<script>

import { buildAnnouncementFragment } from '@/modules/AnnouncementHtml';

// the same Font Awesome the app loads (index.html) - so <i class="fa-solid ..."> works in a paste
const FONT_AWESOME = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';

const BASE_STYLE = `
:host { display: block; }
.puAnnRoot { direction: rtl; text-align: right; color: #2a3142; font-size: 15px; line-height: 1.6; overflow-wrap: break-word; }
.puAnnRoot.padded { padding: 30px 32px 12px; }
.puAnnRoot img, .puAnnRoot video { max-width: 100%; height: auto; }
.puAnnRoot iframe { max-width: 100%; border: 0; }
.puAnnRoot a { color: #6a4fd0; }
.puAnnRoot [data-planup-action], .puAnnRoot [data-planup-link] { cursor: pointer; }
@media (max-width: 576px) { .puAnnRoot.padded { padding: 26px 20px 8px; } }
`;

export default {

    name: 'AnnouncementHtml',

    props: {
        html: { type: String, default: '' },
        padded: { type: Boolean, default: false },
    },

    watch: {
        html() { this.render(); },
        padded() { this.render(); },
    },

    mounted() {

        this.target = this.$el.attachShadow ? this.$el.attachShadow({ mode: 'open' }) : this.$el;
        this.target.addEventListener('click', this.onClick);

        this.render();

    },

    beforeDestroy() {
        if (this.target)
            this.target.removeEventListener('click', this.onClick);
    },

    methods: {

        render() {

            let target = this.target;

            while (target.firstChild)
                target.removeChild(target.firstChild);

            let link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = FONT_AWESOME;
            target.appendChild(link);

            let style = document.createElement('style');
            style.textContent = BASE_STYLE;
            target.appendChild(style);

            let root = document.createElement('div');
            root.className = 'puAnnRoot' + (this.padded ? ' padded' : '');
            root.appendChild(buildAnnouncementFragment(this.html));
            target.appendChild(root);

        },

        onClick(event) {

            let element = event.target;

            while (element && element !== this.target && element.nodeType === 1) {

                let action = element.getAttribute('data-planup-action');
                let link = element.getAttribute('data-planup-link');

                if (action) {
                    event.preventDefault();
                    this.$emit('action', action);
                    return;
                }

                if (link) {
                    event.preventDefault();
                    this.$emit('navigate', link);
                    return;
                }

                if (element.tagName === 'A') {

                    let href = element.getAttribute('href') || '';

                    if (href.charAt(0) === '/' && href.charAt(1) !== '/') {
                        event.preventDefault();
                        this.$emit('navigate', href);
                        return;
                    }

                    if (/^(https?:)?\/\//i.test(href))
                        this.$emit('link', href);
                    else if (!href || href.charAt(0) === '#')
                        event.preventDefault();

                    return;

                }

                element = element.parentNode;

            }

        },

    },

}

</script>
